import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CodeSentResponse,
  TokenResponse,
} from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";
import {
  useRequestCode,
  useVerifyCode,
} from "@/shared/api/generated/auth-controller/auth-controller";
import { Button, Input, Label } from "@/shared/ui";
import { parseApiDate } from "@/shared/lib/date";
import { authErrorMessage } from "../model/errorMessage";
import { DevCodeHint } from "./DevCodeHint";

const CODE_LENGTH = 5;

interface CodeStepProps {
  phone: string;
  sent: CodeSentResponse;
  onResent: (sent: CodeSentResponse) => void;
  onVerified: (tokens: TokenResponse) => void;
  onChangePhone: () => void;
}

/** Step 2 of login: the customer types the SMS code. Submits automatically at 5 digits. */
export function CodeStep({ phone, sent, onResent, onVerified, onChangePhone }: CodeStepProps) {
  const { t } = useTranslation(["auth", "common"]);
  const [code, setCode] = useState("");
  const verify = useVerifyCode();
  const resend = useRequestCode();
  const secondsLeft = useSecondsUntil(sent.lc_resend_available_at);

  const submit = (value: string) => {
    if (value.length !== CODE_LENGTH || verify.isPending) return;
    verify.mutate(
      { data: { lc_phone: phone, lc_code: value } },
      {
        onSuccess: onVerified,
        onError: () => {
          setCode("");
        },
      },
    );
  };

  const error = verify.error ?? resend.error;

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit(code);
      }}
    >
      <p className="text-sm text-muted">{t("auth:codeSentTo", { phone: sent.lc_phone_masked })}</p>

      <div className="flex flex-col gap-2">
        <Label htmlFor="code">{t("auth:codeLabel")}</Label>
        <Input
          id="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LENGTH}
          autoFocus
          className="text-center text-2xl tracking-[0.5em]"
          value={code}
          aria-invalid={verify.isError ? true : undefined}
          onChange={(event) => {
            const digits = event.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH);
            setCode(digits);
            if (digits.length === CODE_LENGTH) submit(digits);
          }}
        />
      </div>

      {import.meta.env.VITE_SHOW_DEV_OTP === "true" && (
        <DevCodeHint
          phone={phone}
          sentAt={sent.lc_expires_at}
          onUse={(devCode) => {
            setCode(devCode);
            submit(devCode);
          }}
        />
      )}

      {error != null && (
        <p role="alert" className="text-sm text-danger">
          {authErrorMessage(error, t)}
        </p>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={code.length !== CODE_LENGTH}
        loading={verify.isPending}
      >
        {t("auth:confirm")}
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <Button type="button" variant="link" size="sm" className="px-0" onClick={onChangePhone}>
          {t("auth:changePhone")}
        </Button>
        {secondsLeft > 0 ? (
          <span className="text-muted">{t("auth:resendIn", { seconds: secondsLeft })}</span>
        ) : (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0"
            disabled={resend.isPending}
            onClick={() => {
              resend.mutate({ data: { lc_phone: phone } }, { onSuccess: onResent });
            }}
          >
            {t("auth:resend")}
          </Button>
        )}
      </div>
    </form>
  );
}

/** Whole seconds left until an API timestamp ("DD-MM-YYYY HH:mm:ss", Tashkent), re-rendering every second. */
function useSecondsUntil(apiTime: string): number {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return Math.max(0, Math.ceil((parseApiDate(apiTime).getTime() - now) / 1000));
}
